import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  pickDefaultShipping, pickDefaultBilling,
  shippingAddresses as filterShipping,
  billingAddresses as filterBilling,
} from './addressDefaults';

/**
 * Hook agnóstico para listar e seleccionar moradas durante o checkout.
 *
 * Expõe um modelo unificado consumido pelos 2 fluxos (MY + CRM Nova
 * Encomenda):
 *
 *   - `addresses`        : todas as moradas devolvidas pelo `fetchAll()`.
 *   - `shippingAddresses`: subset filtrado para envio (R + sem tipo).
 *   - `billingAddresses` : subset filtrado para facturação (F + A).
 *   - `selectedShipping` / `setSelectedShipping`
 *   - `selectedBilling`  / `setSelectedBilling`
 *   - `reload(opts)`     : re-fetcha. Aceita `{ selectId, target }` para
 *                          forçar a selecção depois de criar/editar.
 *
 * @param {object} cfg
 * @param {() => Promise<Array>} cfg.fetchAll
 * @param {string} [cfg.shippingTypeFilter] override do filtro padrão de envio
 * @param {string} [cfg.billingTypeFilter] override do filtro padrão de fact.
 */
export default function useAddressesLogic({ fetchAll }) {
  const [addresses, setAddresses] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState('');
  const [selectedBilling, setSelectedBilling] = useState('');
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async (opts = {}) => {
    const { selectId = null, target = null } = opts;
    setLoading(true);
    try {
      const list = (await fetchAll()) || [];
      setAddresses(list);
      if (selectId && target === 'shipping') {
        setSelectedShipping(String(selectId));
      } else if (selectId && target === 'billing') {
        setSelectedBilling(String(selectId));
      } else {
        const ship = pickDefaultShipping(list);
        if (ship) setSelectedShipping((prev) => prev || String(ship.ID_ClienteMorada ?? ship.ID_Cliente_Morada));
        const bill = pickDefaultBilling(list);
        if (bill) setSelectedBilling((prev) => prev || String(bill.ID_ClienteMorada ?? bill.ID_Cliente_Morada));
      }
      return list;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('useAddressesLogic.reload failed', e);
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchAll]);

  useEffect(() => { reload(); }, [reload]);

  const shippingAddrs = useMemo(() => filterShipping(addresses), [addresses]);
  const billingAddrs = useMemo(() => filterBilling(addresses), [addresses]);

  return {
    addresses, loading,
    shippingAddresses: shippingAddrs,
    billingAddresses: billingAddrs,
    selectedShipping, setSelectedShipping,
    selectedBilling, setSelectedBilling,
    reload,
  };
}
